import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// ─── Section-based menu model (extend with new `type` discriminators as needed) ───

export type ContextMenuAction = {
  id: string
  label: string
  onClick: () => void
  icon?: React.ReactNode
}

export type ContextMenuSection =
  | { type: 'custom'; render: () => React.ReactNode }
  | { type: 'actions'; label?: string; actions: ContextMenuAction[] }

export type ContextMenuPosition = { x: number; y: number }

export function useContextMenu() {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 })

  const openAt = useCallback((clientX: number, clientY: number) => {
    setPosition({ x: clientX, y: clientY })
    setOpen(true)
  }, [])

  const closeMenu = useCallback(() => setOpen(false), [])

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      openAt(e.clientX, e.clientY)
    },
    [openAt]
  )

  return { open, position, openAt, closeMenu, onContextMenu }
}

type ContextMenuProps = {
  open: boolean
  position: ContextMenuPosition
  onClose: () => void
  items: ContextMenuSection[]
  /** Optional vertical offset from anchor (px) */
  offsetY?: number
}

/**
 * Fixed-position context menu portaled to `document.body`.
 * Closes on outside mousedown, Escape, or after an action (caller may call onClose).
 */
export function ContextMenu({ open, position, onClose, items, offsetY = 0 }: ContextMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<ContextMenuPosition>(() => ({
    x: position.x,
    y: position.y + offsetY,
  }))

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return
    const el = panelRef.current
    const rect = el.getBoundingClientRect()
    let x = position.x
    let y = position.y + offsetY
    const pad = 8
    if (x + rect.width > window.innerWidth - pad) {
      x = Math.max(pad, window.innerWidth - rect.width - pad)
    }
    if (y + rect.height > window.innerHeight - pad) {
      y = Math.max(pad, window.innerHeight - rect.height - pad)
    }
    if (x < pad) x = pad
    if (y < pad) y = pad
    setCoords({ x, y })
  }, [open, position.x, position.y, offsetY])

  useEffect(() => {
    if (!open) return
    function handlePointerDown(e: MouseEvent) {
      if (panelRef.current?.contains(e.target as Node)) return
      onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handlePointerDown, true)
    document.addEventListener('keydown', handleKey, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKey, true)
    }
  }, [open, onClose])

  if (!open) return null

  const panel = (
    <div
      ref={panelRef}
      role="menu"
      style={{
        position: 'fixed',
        left: coords.x,
        top: coords.y,
        zIndex: 10000,
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        padding: '8px 10px',
        minWidth: '140px',
        maxWidth: 'min(320px, calc(100vw - 16px))',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((section, idx) => (
        <div key={idx} style={{ marginTop: idx > 0 ? 8 : 0 }}>
          {section.type === 'custom' ? (
            section.render()
          ) : (
            <>
              {section.label ? (
                <div
                  className="eyebrow"
                  style={{ marginBottom: 6, opacity: 0.85, fontSize: '0.65rem' }}
                >
                  {section.label}
                </div>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {section.actions.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      a.onClick()
                      onClose()
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-serif)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-ink)',
                      padding: '6px 8px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--color-paper-dark)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    {a.icon ? <span style={{ flexShrink: 0 }}>{a.icon}</span> : null}
                    {a.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  )

  return createPortal(panel, document.body)
}
